/***********************************************
ICEMEDX - Developed By ICEMEDX, Inc.
Copyright 2018 ICEMEDX, Inc.
***********************************************/
import { MatTableDataSource } from '@angular/material';
import { FormControl, FormGroup, NgForm, Validators, ValidatorFn } from '@angular/forms';
import { Iceutility } from 'projects/icemedx-lib/src/services/iceutility';
import { userTypes } from 'projects/icemedx-lib/src/login/login.config';
import { EventEmitter } from '@angular/core';

declare var google: any;
var googlechartSettings = {};
var googlecurrent = [];

export enum ChartTypes {
  ctNone = 0,
  ctPie = 1,
  ctBubble = 2,
  ctBar = 3
}

export enum PaymentMethods {
  pmNone = -1,
  pmCash = 0,
  pmCreditCard = 1,
  pmBankAccount = 2,
}

export interface iceSettings {
  tag: string;
}

export class iceColumnDefinition {
  columnName: string = "";
  displayName: string = "";
  primaryKey: boolean = false;
  hidden: boolean = false;
  important: boolean = true;
  editProperties: IcecontrolSettings;

  constructor(col: string, disCol: string, primKey: boolean, hide?: boolean, properties?: IcecontrolSettings, important: boolean = true) {
    this.columnName = col;
    this.displayName = disCol;
    this.primaryKey = primKey;
    this.hidden = hide;
    this.important = important;
    this.editProperties = properties;
  }
}

export class IcecontrolPermission {
  feature: userTypes.USER_FEATURES = userTypes.USER_FEATURES.ufNone;
  default_access: userTypes.USER_FEATURE_ACCESS = userTypes.USER_FEATURE_ACCESS.ufaReadOnly;

  constructor(ft: userTypes.USER_FEATURES, access: userTypes.USER_FEATURE_ACCESS) {
    this.feature = ft;
    this.default_access = access;
  }
}

export class IcecontrolSettings implements iceSettings {
  tag: string = "";
  placeholder: string = "";
  properties: any = {};
  jsEvents: any = {};
  tsEvents: any = {};
  id: string = "";
  controlName: string = "";

  private options: any[] = [];
  optionAutoCompleteFormControl: FormControl = null;
  readonly: boolean = false;
  disabled: boolean = false;
  multipleSelection: boolean = false;
  permissions: IcecontrolPermission = null;
  actionItems: any[] = [];

  controlChange: EventEmitter<any> = null;
  controlClick: EventEmitter<any> = null;
  actionClick: EventEmitter<any> = null;

  private formControl: FormControl = null;

  constructor(tg: string, props: any, jsEvts: any, tsEvts: any, id: string = "", placeholder: string = "", perms: IcecontrolPermission = null) {
    this.tag = tg;
    this.properties = props;
    this.jsEvents = jsEvts;
    this.tsEvents = tsEvts;
    this.id = id;
    this.placeholder = placeholder;
    this.permissions = perms;

    if (tg == "icegooglechart" && id != null && id.length > 0)
      googlechartSettings[id] = this;
  }

  setControlName(name?: string): IcecontrolSettings {
    try {
      this.controlName = name == null ? "ice-" + Iceutility.guid(false) : name;
    }
    catch (e) {
      console.log(e);
    }
    return this;
  }

  getControlName(): string {
    return this.controlName;
  }

  setOptions(opts: any[], resetValue?: boolean): IcecontrolSettings {
    this.options = opts;
    if (this.properties != null) this.properties.menuItems = opts;

    if (this.optionAutoCompleteFormControl != null && (resetValue == null || resetValue == true)) this.optionAutoCompleteFormControl.reset();
    return this;
  }
  
  getOptions(): any[] {
    return this.options == null ? [] : this.options;
  }

  setReadonly(ro: boolean): IcecontrolSettings {
    try {
      this.readonly = ro;
    }
    catch (e) {
      console.log(e);
    }
    return this;
  }

  getReadonly(): boolean {
    return this.readonly;
  }

  setDisabled(dis: boolean): IcecontrolSettings {
    try {
      this.disabled = dis;
    }
    catch (e) {
      console.log(e);
    }
    return this;
  }

  getDisabled(): boolean {
    return this.disabled;
  }

  
  setMultipleSelection(multiSelect: boolean): IcecontrolSettings {
    try {
      this.multipleSelection = multiSelect;
    }
    catch (e) {
      console.log(e);
    }
    return this;
  }
  getMultipleSelection(): boolean {
    return this.multipleSelection;
  }

  createFormControl(formState: any, validators: ValidatorFn[]): IcecontrolSettings {
    try {
      if (this.formControl != null) return this;

      this.formControl = new FormControl(formState, validators);
    }
    catch (e) {
      console.log(e);
    }
    return this;
  }

  registerFormControl(name: string, frmGrp: FormGroup): IcecontrolSettings {
    try {
      if (frmGrp == null) return this;
      if (this.formControl == null) return this;
      if (name == null || name.length == 0) name = this.getControlName();
      frmGrp.registerControl(name, this.formControl);
    }
    catch (e) {
      console.log(e);
    }
    return this;
  }

  getFormControl(): FormControl {
    return this.formControl;
  }

  createChangeControlEvent(): EventEmitter<any> {
    try {
      if (this.controlChange == null)
        this.controlChange = new EventEmitter<any>();
    }
    catch (e) {
      console.log(e);
    }
    return this.controlChange;
  }

  getChangeControlEvent(): EventEmitter<any> {
    return this.controlChange;
  }

  createClickControlEvent(): EventEmitter<any> {
    try {
      if (this.controlClick == null)
        this.controlClick = new EventEmitter<any>();
    }
    catch (e) {
      console.log(e);
    }
    return this.controlClick;
  }

  getClickControlEvent(): EventEmitter<any> {
    return this.controlClick;
  }

  setActionItems(items: any[]): IcecontrolSettings {
    try {
      this.actionItems = items;
    }
    catch (e) {
      console.log(e);
    }
    return this;
  }
  getActionItems(): any[] {
    return this.actionItems;
  }

  createActionClickItemEvent(): EventEmitter<any> {
    try {
      if (this.actionClick == null)
        this.actionClick = new EventEmitter<any>();
    }
    catch (e) {
      console.log(e);
    }
    return this.actionClick;
  }

  getActionClickItemEvent(): EventEmitter<any> {
    return this.actionClick;
  }

  loadGoogleChart(id: string, type: ChartTypes): void {
    if (id == null || id.length == 0) return;

    google.charts.load('current', { 'packages': ['corechart'] });

    switch (type) {
      case ChartTypes.ctPie:
        googlecurrent.push(id);
        google.charts.setOnLoadCallback(this.drawPieChart);
        break;
      case ChartTypes.ctBubble:
        googlecurrent.push(id);
        google.charts.setOnLoadCallback(this.drawSeriesChart);
        break;
      case ChartTypes.ctBar:
        googlecurrent.push(id);
        google.charts.setOnLoadCallback(this.drawBarChart);
        break;
    }
  }

  private drawPieChart(): void {
    if (googlecurrent.length == 0) return;
    try {
      let id = googlecurrent.shift();
      let chartSet: IcecontrolSettings = googlechartSettings[id];
      let datAry: any[] = [];
      if (chartSet != null) {
        let opts = chartSet.getOptions();
        for (let iopt = 0; iopt < opts.length; iopt++) {
          let opt = opts[iopt];
          if (opt == null) continue;

          datAry.push([opt.name, opt.value]);
        }

        if (datAry.length == 0) return;

        var data = google.visualization.arrayToDataTable(datAry);
        var options = {
          title: chartSet.placeholder,
          sliceVisibilityThreshold: .05
        };

        var chart = new google.visualization.PieChart(document.getElementById(id));
        chart.draw(data, options);
      }
    }
    catch (e) {
      console.log(e);
    }
  }

  private drawSeriesChart(): void {
    if (googlecurrent.length == 0) return;
    try {

      let id = googlecurrent.shift();
      let chartSet: IcecontrolSettings = googlechartSettings[id];
      if (chartSet != null) {

        var data = google.visualization.arrayToDataTable(chartSet.getOptions());
        var chart = new google.visualization.BubbleChart(document.getElementById(id));
        chart.draw(data, chartSet.properties.chartOptions);
      }
    }
    catch (e) {
      console.log(e);
    }
  }

  private drawBarChart() {
    if (googlecurrent.length == 0) return;
    try {
      let id = googlecurrent.shift();
      let chartSet: IcecontrolSettings = googlechartSettings[id];
      if (chartSet != null) {

        var options = [];
        let opts = chartSet.getOptions();
        for (let iopt = 0; iopt < opts.length; iopt++) {
          let opt = opts[iopt];
          if (opt == null) continue;

          let point = [];
          if (iopt == 0) {
            if (opt["name"] != null) {
              point.push(opt["name"]);
            }
            else {
              point.push("Element");
            }

            if (opt["value"] != null) {
              point.push(opt["value"]);
            }
            else {
              point.push("Values");
            }

            point.push({ role: "style" });
            options.push(point);
            continue;
          }

          if (opt["name"] != null) {
            point.push(opt["name"]);
          }
          if (opt["value"] != null) {
            point.push(Iceutility.toDouble(opt["value"]));
          }
          if (opt["color"] != null) {
            point.push(opt["color"]);
          }
          else {
            point.push("#b87333");
          }
          options.push(point);
        }

        var data = google.visualization.arrayToDataTable(options);
        var view = new google.visualization.DataView(data);
        view.setColumns([0, 1, {
            calc: "stringify",
            sourceColumn: 1,
            type: "string",
            role: "annotation"
          },
          2]);

        var chartOptions = {
          title: chartSet.placeholder,
          width: 600,
          height: 400,
          bar: { groupWidth: "95%" },
          legend: { position: "none" },
        };

        var chart = new google.visualization.BarChart(document.getElementById(id));
        chart.draw(view, chartOptions/*chartSet.properties.chartOptions*/);
      }
    }
    catch (e) {
      console.log(e);
    }
  }
}

export class IcemattableSettings implements iceSettings {
  tag: string = "";
  iColumns: iceColumnDefinition[];
  iRows: MatTableDataSource<any> = null;
  actionItems: any[] = [];
  enableRowSelection: boolean;
  events: {};
  doInlineEdit: boolean;
  hidePaginator: boolean = false;
  addButton: boolean = false;
  showTable: boolean = true;
  loadingIndicator: boolean;

  primaryKeyColumn: string = "";
  formRowGroup: any = {};

  permissions: IcecontrolPermission = null;

  constructor(tg: string, icols: iceColumnDefinition[], irows: MatTableDataSource<any>, actionItems: any[],
    enableRowSelection: boolean = false,
    events: any = null, doInlineEdit: boolean = false, perms: IcecontrolPermission = null, loadingIndicator: boolean = false) {
      this.tag = tg;
      this.iColumns = icols;
      this.iRows = irows;
	    this.actionItems = actionItems;
      this.enableRowSelection = enableRowSelection;
      this.events = events;
      this.doInlineEdit = doInlineEdit;
      this.permissions = perms;
      this.loadingIndicator = loadingIndicator;
  }

  getTableType() {
    return this.constructor.name;
  }

  getFormGroup(rowData: any, createFormControls?: boolean): FormGroup {
    let formGrp: FormGroup = null;

    try {
      if (rowData == null) { // no record so create group to prevent compilation errors
        return new FormGroup({});
      }

      let key: any = rowData[this.getPrimaryKey()];
      if (key == null) return new FormGroup({});

      formGrp = <FormGroup>this.formRowGroup[key];
      if (formGrp == null) {        
        let ctrls = {};
        if (createFormControls == true) {
          let cols = this.getColumns();
          for (let icol = 0; icol < cols.length; icol++) {
            let colDef = cols[icol];
            if (colDef == null) continue;

            let ctrl = colDef.editProperties;
            if (ctrl == null) continue;

            let fctrl: FormControl = ctrl.getFormControl();
            if (fctrl == null)
              fctrl = new FormControl();

            fctrl.setValue(rowData[colDef.columnName]);
            ctrls[colDef.columnName] = fctrl;
          }
        }
        formGrp = new FormGroup(ctrls);
        this.formRowGroup[key] = formGrp;
      }
    }
    catch (e) {
      console.log(e);
    }

    return formGrp;
  }

  getFormGroupByPrimaryKey(key: string): FormGroup {
    if (key == null || key.length == 0) return null;

    let formGrp: FormGroup = null;

    try {
      formGrp = <FormGroup>this.formRowGroup[key];
    }
    catch (e) {
      console.log(e);
    }

    return formGrp;
  }

  getPrimaryKey(): string {
    if (this.primaryKeyColumn != null && this.primaryKeyColumn.length > 0) return this.primaryKeyColumn;

    if (this.iColumns == null)
      return "";

    let keyCols = this.iColumns
      .filter(colDef => colDef.primaryKey === true)
      .map(colDef => colDef.columnName);

    if (keyCols != null && keyCols.length > 0) {
      this.primaryKeyColumn = keyCols[0];
      return this.primaryKeyColumn;
    }

    this.primaryKeyColumn = this.iColumns[0].columnName;
    return this.primaryKeyColumn;
  }

  getDisplayName(colName: string): string {
    for (let idx in this.iColumns) {
      let colDef: iceColumnDefinition = this.iColumns[idx];
      if (colDef == null) continue;
      if (colDef.columnName == colName) {
        return colDef.displayName;
      }
    }
    return colName;
  }

  getColumn(colName: string): iceColumnDefinition {
    for (let idx in this.iColumns) {
      let colDef: iceColumnDefinition = this.iColumns[idx];
      if (colDef == null) continue;
      if (colDef.columnName == colName) {
        return colDef;
      }
    }
    return null;
  }

  getColumns(includeImportant: boolean = true): iceColumnDefinition[] {
    if (this.iColumns == null)
      return [];

    return this.iColumns
      .filter(colDef => (colDef.hidden === false || colDef.hidden == null) && colDef.primaryKey === false);
  }

  getColumnNames(): string[] {
    if (this.iColumns == null)
      return [];

    return this.getColumns()
      .map(colDef => colDef.columnName);
  }

  showAddButton(show: boolean): IcemattableSettings {
    try {
      this.addButton = show;
    }
    catch (e) {
      console.log(e);
    }
    return this;
  }

  setShowTable(show: boolean): IcemattableSettings {
    try {
      this.showTable = show;
    }
    catch (e) {
      console.log(e);
    }
    return this;
  }
}

export class IcepanelgroupSettings implements iceSettings {
  tag: string = "";
  controls: IcecontrolSettings[];
  tables: IcemattableSettings[];

  permissions: IcecontrolPermission = null;

  constructor(ctrls: IcecontrolSettings[], tables: IcemattableSettings[], perms: IcecontrolPermission = null) {
    this.controls = ctrls;
    this.tables = tables;
    this.permissions = perms;
  }
}

export enum SECTION_CONTROL_ALIGNMENT {
  ctrlAlignHorizontal = 0,
  ctrlAlignVertical = 1,
}

export class IcepanelsectionSettings implements iceSettings {
  tag: string = "";
  properties: any = {};
  groups: IcepanelgroupSettings[];
  controls: IcecontrolSettings[];
  tables: IcemattableSettings[];
  controlAlignment: SECTION_CONTROL_ALIGNMENT = SECTION_CONTROL_ALIGNMENT.ctrlAlignHorizontal;
  show: boolean = true;
  useSpacing: boolean = true;
  loading: boolean = false;
  permissions: IcecontrolPermission = null;

  constructor(tg: string, props: any, grps: IcepanelgroupSettings[], ctrls: IcecontrolSettings[], tbls: IcemattableSettings[], perms: IcecontrolPermission = null) {
    this.tag = tg;
    this.properties = props;
    this.groups = grps;
    this.controls = ctrls;
    this.tables = tbls;
    this.permissions = perms;
  }

  setControlAlignment(ctrlAlign: SECTION_CONTROL_ALIGNMENT): IcepanelsectionSettings {
    this.controlAlignment = ctrlAlign;
    return this;
  }

  getControlAlignment(): SECTION_CONTROL_ALIGNMENT {
    return this.controlAlignment;
  }

  setShow(show: boolean): IcepanelsectionSettings {
    try {
      this.show = show;
    }
    catch (e) {
      console.log(e);
    }
    return this;
  }

  setLoading(loading: boolean): IcepanelsectionSettings {
    try {
      this.loading = loading;
    }
    catch (e) {
      console.log(e);
    }
    return this;
  }

  getShow(): boolean {
    return this.show;
  }

  isLoading(): boolean {
    return this.loading;
  }

  setUseSpacing(spacing: boolean): IcepanelsectionSettings {
    try {
      this.useSpacing = spacing;
    }
    catch (e) {
      console.log(e);
    }
    return this;
  }
}

export class IcepanelSettings implements iceSettings {
  tag: string;
  panelKey: string;
  title: string;
  id: string;
  headerControls: IcecontrolSettings[];
  sections: IcepanelsectionSettings[];
  loading: boolean = false;

  private formGroup: FormGroup = null;

  permissions: IcecontrolPermission = null;

  initializeEvent: EventEmitter<any> = null;

  constructor(tg: string, key: string, title: string, id: string,
    secs: IcepanelsectionSettings[],
    headControls: IcecontrolSettings[] = [], perms: IcecontrolPermission = null) {
    this.tag = tg;
    this.panelKey = key;
    this.title = title;
    this.id = id;
    this.sections = secs;
    this.headerControls = headControls;
    this.permissions = perms;
  }

  isFormEnabled(): boolean {
    return this.formGroup != null;
  }

  getForm(): FormGroup{
    return this.formGroup;
  }

  enableForm(frm?: FormGroup): IcepanelSettings {
    try {
      if (this.formGroup == null || frm != null) {       
        let ctrls = {};
        for (let isec = 0; isec < this.sections.length; isec++) {
          let sec: IcepanelsectionSettings = this.sections[isec];
          if (sec == null) continue;

          let controls: IcecontrolSettings[] = [];
          if (sec.controls != null) controls = controls.concat(sec.controls);
          if (sec.groups != null) {
            for (let igrp = 0; igrp < sec.groups.length; igrp++) {
              let grp = sec.groups[igrp];
              if (grp == null) continue;

              // Complete  if data source is present for table
              for (let itab = 0; itab < grp.tables.length; itab++) {
                let tab = grp.tables[itab];
                if (tab == null) continue;

                // loop row tab.iRows
                // let frmGrp tab.getFormGroup(row)
              }
            }
          }

          for (let ictrl = 0; ictrl < controls.length; ictrl++) {
            let ctrl: IcecontrolSettings = controls[ictrl];
            if (ctrl == null) continue;

            let fctrl: FormControl = ctrl.getFormControl();
            if (fctrl == null)
              fctrl = new FormControl();

            ctrls[ctrl.getControlName()] = fctrl;
          }
        }
        if (frm == null)
          this.formGroup = new FormGroup(ctrls);
        else
          this.formGroup = frm;
      }
    }
    catch (e) {
      console.log(e);
    }
    return this;
  }

  setLoading(loading: boolean): IcepanelSettings {
    try {
      this.loading = loading;
    }
    catch (e) {
      console.log(e);
    }
    return this;
  }

  isLoading(): boolean {
    return this.loading;
  }

  createInitializeEvent(): EventEmitter<any> {
    try {
      if (this.initializeEvent == null)
        this.initializeEvent = new EventEmitter<any>();
    }
    catch (e) {
      console.log(e);
    }
    return this.initializeEvent;
  }

  getInitializeEvent(): EventEmitter<any> {
    return this.initializeEvent;
  }
}

export class IceexpansionpanelSettings extends IcepanelSettings {
  description: string = "";
  matIcons: IcecontrolSettings[];
  matActions: IcecontrolSettings[];
  stepAssignment: number = -1;

  constructor(
      tg: string,
      key: string,
      title: string,
      id: string,
      secs: IcepanelsectionSettings[],
      headControls: IcecontrolSettings[] = [],
      icons: IcecontrolSettings[] = [],
      actions: IcecontrolSettings[] = [],
      step: number = -1) {

    super(tg, key, title, id, secs, headControls);
    this.matIcons = icons;
    this.matActions = actions;
    this.stepAssignment = step;
  }
}

export class IcepanelcontainerRowSettings {
  columns: IcepanelSettings[];

  constructor(cols: IcepanelSettings[]) {
    this.columns = cols;
  }
}

export class IcepanelcontainerSettings {
  containerId: string = "";
  rows: IcepanelcontainerRowSettings[] = [];
  private currentStep: number = 0;
  constructor(id: string, rows: IcepanelcontainerRowSettings[]) {
    this.containerId = id;
    this.rows = rows;
  }

  setStep(step: number): IcepanelcontainerSettings {
    this.currentStep = step;
    return this;
  }

  getStep(): number {
    return this.currentStep;
  }
}


export class IcedashboardiconSettings {
  messageCount: number = 0;
  messageRoute: string = "";

  ordersCount: number = 0;
  ordersRoute: string = "";

  tasksCount: number = 0;
  tasksRoute: string = "";

  alertsCount: number = 0;
  alertsRoute: string = "";
}

export class IceResponsiveMattableSettings extends IcemattableSettings {
  constructor(tg: string, icols: iceColumnDefinition[], irows: MatTableDataSource<any>, actionItems: any[],
    enableRowSelection: boolean = false,
    events: any = null, hasInlineControls: boolean = false, perms: IcecontrolPermission = null, loadingIndicator: boolean = false) {
    super(tg, icols, irows, actionItems, enableRowSelection, events, hasInlineControls, perms, loadingIndicator);
  }
}

export class IceResponsiveMattableEditSettings extends IcemattableSettings {
  constructor(tg: string, icols: iceColumnDefinition[], irows: MatTableDataSource<any>, actionItems: any[],
    enableRowSelection: boolean = false,
    events: any = null, hasInlineControls: boolean = false, perms: IcecontrolPermission = null, loadingIndicator: boolean = false) {
    super(tg, icols, irows, actionItems, enableRowSelection, events, hasInlineControls, perms, loadingIndicator);
  }
}
